import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCreateCreatorPost } from '@/hooks/use-mutations'

const postFormSchema = z.object({
  platform: z.enum(['instagram', 'tiktok'] as const),
  postType: z.enum(['reel', 'story', 'feed', 'tiktok', 'carousel'] as const),
  postUrl: z.string().url('URL inválida').or(z.literal('')),
  postedAt: z.string().optional(),
  caption: z.string().optional(),
})

type PostFormValues = z.infer<typeof postFormSchema>

interface PostFormDialogProps {
  creatorId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Render a modal form for creating a creator post.
 *
 * The form validates input using the file's schema, adjusts available post types based on the
 * selected platform, omits an empty `postUrl` from the submission payload, and normalizes
 * `postedAt` to an ISO timestamp with time set to 12:00:00Z before sending. On successful
 * submission the form is reset and the dialog is closed.
 *
 * @param creatorId - ID of the creator the new post will be associated with
 * @param open - Controls whether the dialog is visible
 * @param onOpenChange - Callback invoked when the dialog open state changes
 * @returns The dialog element containing the post creation form
 */
export function PostFormDialog({ creatorId, open, onOpenChange }: PostFormDialogProps) {
  const createPost = useCreateCreatorPost()

  const form = useForm<PostFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(postFormSchema) as any,
    defaultValues: {
      platform: 'instagram',
      postType: 'story',
      postUrl: '',
      postedAt: new Date().toISOString().split('T')[0],
      caption: '',
    },
  })

  // Watch platform to reset type if needed
  const watchPlatform = useWatch({ control: form.control, name: 'platform' })

  function onSubmit(data: PostFormValues) {
    // Clean empty string
    const payload: Partial<PostFormValues> = { ...data }
    if (!payload.postUrl) {
      delete payload.postUrl
    }

    // Ensure date has time
    if (payload.postedAt) {
      payload.postedAt = new Date(`${payload.postedAt}T12:00:00Z`).toISOString()
    }

    createPost.mutate(
      { ...payload, creatorId } as import('@/lib/api/types').CreateCreatorPostInput,
      {
        onSuccess: () => {
          form.reset()
          onOpenChange(false)
        },
      }
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Registrar Novo Post</SheetTitle>
          <SheetDescription>
            Atualmente métricas não são puxadas de forma automática neste MVP. Guarde a URL como
            evidência.
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col flex-1 overflow-hidden"
          >
            <SheetBody className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="platform"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Plataforma</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="instagram">Instagram</SelectItem>
                          <SelectItem value="tiktok">TikTok</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="postType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Formato</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {watchPlatform === 'instagram' ? (
                            <>
                              <SelectItem value="story">Story</SelectItem>
                              <SelectItem value="reel">Reel</SelectItem>
                              <SelectItem value="feed">Post no Feed</SelectItem>
                              <SelectItem value="carousel">Carrossel</SelectItem>
                            </>
                          ) : (
                            <SelectItem value="tiktok">TikTok Video</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="postedAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data da Publicação</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="postUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Link da Publicação</FormLabel>
                    <FormControl>
                      <Input placeholder="https://instagram.com/p/..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="caption"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Legenda Resumida (Opcional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Ex: @Nuture Cupom: GURU832"
                        className="resize-none"
                        rows={3}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </SheetBody>

            <SheetFooter>
              <ChonkyButton type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Cancelar
              </ChonkyButton>
              <ChonkyButton type="submit" loading={createPost.isPending}>
                Salvar Publicação
              </ChonkyButton>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}
